import { ChevronDownIcon, ChevronUpIcon, DeleteIcon, TimeIcon } from '@chakra-ui/icons';
import { Box, Button, Flex, Input, InputGroup, InputRightAddon, Select, Text, useColorModeValue } from '@chakra-ui/react';
import currencySymbolMap from 'currency-symbol-map';
import moment from 'moment';
import { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { IoIosAddCircleOutline } from 'react-icons/io';
import { TbInfoTriangle } from 'react-icons/tb';
import BudgetTimeSelector from './BudgetTimeSelector';

function formatDate(dateObj) {
  if (!dateObj) return '';
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(dateObj);
}

const BudgetScheduleSpecs = ({ specs, currency, setFieldValue, onOverlapErrorChange } ) => {

  const backgroundColor = useColorModeValue('gray.50', 'gray.700');
  const boxBg = useColorModeValue('gray.100', 'gray.700');
  const inputBackground = useColorModeValue('#F5F3F9', '#1e1530');
  const mainBackgroundColor = useColorModeValue('gray.100', 'gray.800');
  const hoverBgColor = useColorModeValue('gray.200', 'gray.900');
  const borderColor = useColorModeValue('gray.200', 'inherit');
  const hoverBorderColor = useColorModeValue('gray.300', 'gray.700');
  const inputTextColor = useColorModeValue('#421e66', '#b5b5cd')

  const currencyCode = currency
  const currencySymbol = currencySymbolMap(currencyCode);

  const [expandedIndexes, setExpandedIndexes] = useState(specs.map(() => true));
  const [budgetValueInputs, setBudgetValueInputs] = useState(specs.map(p => p.budget_value));
  const [overlappingIndexes, setOverlappingIndexes] = useState([]);

  useEffect(() => {
    setBudgetValueInputs(specs.map(p => p.budget_value));
  }, [specs]);

  useEffect(() => {
    if (typeof onOverlapErrorChange === 'function') {
      onOverlapErrorChange(overlappingIndexes.length > 0);
    }
  }, [overlappingIndexes, onOverlapErrorChange]);
  
  useEffect(() => {
    const detectOverlaps = (periods) => {
      const overlaps = [];
  
      for (let i = 0; i < periods.length; i++) {
        const aStart = periods[i].time_start;
        const aEnd = periods[i].time_end;
  
        for (let j = 0; j < periods.length; j++) {
          if (i === j) continue;
          const bStart = periods[j].time_start;
          const bEnd = periods[j].time_end;
  
          if (aStart < bEnd && bStart < aEnd) {
            overlaps.push(i);
            break;
          }
        }
      }
  
      return overlaps;
    };
  
    const overlaps = detectOverlaps(specs);
    setOverlappingIndexes(overlaps);
  }, [specs]);

  const handleToggleExpand = (index) => {
    const updated = [...expandedIndexes];
    updated[index] = !updated[index];
    setExpandedIndexes(updated);
  };
  
  const handleAddPeriod = () => {
    if (specs.length >= 50) return;
  
    const lastEndDate = specs.length > 0 
      ? moment.unix(specs[specs.length - 1].time_end) 
      : moment().add(1, 'day').startOf('day'); // Start from tomorrow if no periods
  
    const startDate = moment(lastEndDate).startOf('day'); // Start at midnight of next day
    const endDate = moment(startDate).add(1, 'day').startOf('day'); // End next day midnight
  
    const newPeriod = {
      time_start: startDate.unix(),
      time_end: endDate.unix(),
      budget_value_type: 'ABSOLUTE',
      budget_value: 5
    };
  
    setFieldValue('budget_schedule_specs', [...specs, newPeriod]);
    setExpandedIndexes([...expandedIndexes, true]);
  };
  

  const handleRemovePeriod = (index) => {
    const updatedSpecs = specs.filter((_, i) => i !== index);
    const updatedExpanded = expandedIndexes.filter((_, i) => i !== index);
    setFieldValue('budget_schedule_specs', updatedSpecs);
    setExpandedIndexes(updatedExpanded);
  };

  const updateStartTime = (startDate, time) => {
    return moment(startDate)
      .set({
        hours: parseInt(time.split(":"[0]), 10),
        minutes: parseInt(time.split(":"[1]), 10),
        seconds: 0,
      })
      .unix();
  };

  const updateEndTime = (endDate, time) => {
    return moment(endDate)
      .set({
        hours: parseInt(time.split(":"[0]), 10),
        minutes: parseInt(time.split(":"[1]), 10),
        seconds: 0,
      })
      .unix();
  };

  const timestampToDate = (ts) => ts ? new Date(ts * 1000) : null;
  const timestampToTimeString = (ts) => ts ? moment.unix(ts).format('HH:mm') : '';

  const handleChange = (index, field, value) => {
    const updated = specs.map((item, i) => {
      if (i !== index) return item;
  
      const updatedItem = { ...item, [field]: value };
  
      // Ensure time_end is after time_start
      if (field === 'time_start' && updatedItem.time_end <= value) {
        // Add 1 hour (3600 seconds) or push to next day
        updatedItem.time_end = value + 86400;
      }
  
      return updatedItem;
    });
  
    setFieldValue('budget_schedule_specs', updated);
  };
  

  return (
    <Flex direction="column" gap={2}>
      {specs?.map((period, index) => {
        const startDateObj = timestampToDate(period.time_start);
        const startTimeStr = timestampToTimeString(period.time_start);
        const endDateObj = timestampToDate(period.time_end);
        const endTimeStr = timestampToTimeString(period.time_end);

        return (
          <Box 
            key={index} 
            border="1px solid" 
            borderColor={borderColor} 
            p={expandedIndexes[index] ? 2 : 1} 
            px={2}
            borderRadius={6} 
            bg={mainBackgroundColor}  
            _hover={{ bg: hoverBgColor }} 
            mb={expandedIndexes[index] ? 2 : 0} 
          >
           <Button
              onClick={() => handleToggleExpand(index)}
              variant="none"
              width="100%"
              justifyContent="space-between"
              alignItems="center"
              pb={expandedIndexes[index] ? 1 : 0}
              px={0}
              size={'sm'}
            >
              <Text fontWeight={'normal'} textAlign="left">
                {expandedIndexes[index]
                  ? 'Time period for budget increase'
                  : `Spend ${currencySymbol}${parseFloat(period.budget_value) + 20} as the daily budget from ${formatDate(startDateObj)} to ${formatDate(endDateObj)}`}
              </Text>
              {expandedIndexes[index] ? <ChevronUpIcon boxSize={5}/> : <ChevronDownIcon boxSize={5}/>}
            </Button>

            {expandedIndexes[index] && (
              <Flex direction={'column'} px={4} pb={4} pt={2} bg={backgroundColor} gap={4} borderRadius={'sm'}>
                <Flex gap={1}>
                  <Flex direction="column" flex={'48%'}>
                    <Text mb={1} fontSize={'14px'} fontWeight={'bold'}>Starts on</Text>
                      <Flex align="center" gap={2} border={'1px solid'} bg={inputBackground} borderColor={borderColor} _hover={{ borderColor: hoverBorderColor }} borderRadius={'md'}>
                        <Flex alignItems={'center'} justifyContent={'center'} flex={0.5} p={0.5}>
                          <DatePicker
                            selected={startDateObj}
                            onChange={(date) => {
                              const updatedTs = updateStartTime(date, startTimeStr || '00:00');
                              handleChange(index, 'time_start', updatedTs);
                            }}
                            minDate={new Date()}
                            dateFormat="dd/MM/yyyy"
                            customInput={
                              <input
                                type="text"
                                className="datepicker-input"
                                placeholder="Select Date"
                                style={{
                                  fontSize: '13px',
                                  padding: '4px',
                                  border: 'none',
                                  width: '100px',
                                  height: '18px',
                                  color: inputTextColor,
                                  backgroundColor: 'inherit',
                              }}
                              />
                            }
                          />
                        </Flex>
                        <Flex alignItems={'center'} justifyContent={'center'} flex={0.5} gap={2} borderLeft={'1px solid'} borderColor={borderColor} _hover={{ borderColor: hoverBorderColor }} p={0.5}>
                        <TimeIcon color={inputTextColor} boxSize={3.5} ml={2}/>
                        <BudgetTimeSelector
                          value={startTimeStr}
                          onChange={(newTime) => {
                            const updatedTs = updateStartTime(startDateObj || new Date(), newTime);
                            handleChange(index, 'time_start', updatedTs);
                          }}
                        />
                        </Flex>
                      </Flex>
                  </Flex>
                  <Flex flex={'4%'} alignItems={'center'} justifyContent={'center'} mt={5}>
                    -
                  </Flex>
                  <Flex direction="column" flex={'48%'}>
                    <Text mb={1} fontSize={'14px'} fontWeight={'bold'}>Ends</Text>
                    <Flex align="center" gap={2} border={'1px solid'} bg={inputBackground} borderColor={borderColor} _hover={{ borderColor: hoverBorderColor }} borderRadius={'md'}>
                        <Flex alignItems={'center'} justifyContent={'center'} flex={0.5} p={0.5}>
                          <DatePicker
                            selected={endDateObj}
                            onChange={(date) => {
                              const updatedTs = updateEndTime(date, endTimeStr || '00:00');
                              handleChange(index, 'time_end', updatedTs);
                            }}
                            minDate={new Date()}
                            dateFormat="dd/MM/yyyy"
                            customInput={
                              <input
                                type="text"
                                className="datepicker-input"
                                placeholder="Select Date"
                                style={{
                                  fontSize: '13px',
                                  padding: '4px',
                                  border: 'none',
                                  width: '100px',
                                  height: '18px',
                                  color: inputTextColor,
                                  backgroundColor: 'inherit',
                              }}
                              />
                            }
                          />
                        </Flex>
                        <Flex alignItems={'center'} justifyContent={'center'} flex={0.5} gap={2} borderLeft={'1px solid'} borderColor={borderColor} _hover={{ borderColor: hoverBorderColor }} p={0.5}>
                        <TimeIcon color={inputTextColor} boxSize={3.5} ml={2}/>
                        <BudgetTimeSelector
                          value={endTimeStr}
                          onChange={(newTime) => {
                            const updatedTs = updateEndTime(endDateObj || new Date(), newTime);
                            handleChange(index, 'time_end', updatedTs);
                          }}
                        />
                        </Flex>
                      </Flex>
                  </Flex>
                </Flex>

                <Flex gap={4} align="center">
                  <Flex direction="column" flex={0.7}>
                    <Select
                      size={'sm'}
                      borderRadius="md"
                      value={specs[0]?.budget_value_type}
                      onChange={(e) => {
                        const selectedValue = e.target.value;
                        const updated = specs.map((item) => ({
                          ...item,
                          budget_value_type: selectedValue,
                        }));
                        setFieldValue('budget_schedule_specs', updated);
                      }}
                      isDisabled={index !== 0}
                    >
                      <option value="ABSOLUTE">Increase daily budget by value amount ($)</option>
                      <option value="MULTIPLIER">Increase daily budget by a percentage (%)</option>
                    </Select>
                  </Flex>

                  <Flex direction="column" flex={0.3}>
                    <InputGroup>
                      <Input
                          placeholder={currencySymbol}
                          type="number"
                          size={'sm'}
                          borderRadius="md"
                          value={budgetValueInputs[index]}
                          onChange={(e) => {
                            const updatedInputs = [...budgetValueInputs];
                            updatedInputs[index] = e.target.value;
                            setBudgetValueInputs(updatedInputs);
                          }}
                          onBlur={() => {
                            const inputVal = budgetValueInputs[index];
                            const parsed = parseFloat(inputVal);

                            if (!isNaN(parsed)) {
                              handleChange(index, 'budget_value', parsed);
                            } else {
                              const updatedInputs = [...budgetValueInputs];
                              updatedInputs[index] = specs[index].budget_value;
                              setBudgetValueInputs(updatedInputs);
                            }
                          }}
                        />
                        <InputRightAddon height={'32px'} fontSize={'14px'}>
                            {currencyCode}
                        </InputRightAddon>
                    </InputGroup>
                  </Flex>
                </Flex>

                <Flex>
                  <Text fontSize={'13px'}>{`Meta will aim to spend an average of ${currencySymbol}${parseFloat(period.budget_value) + 20} a day (a ${currencySymbol}${parseFloat(period.budget_value)} increase) from ${formatDate(startDateObj)} to ${formatDate(endDateObj)}.`}</Text>
                </Flex>

                <Flex>
                    <Button
                      leftIcon={<DeleteIcon />}
                      colorScheme="gray"
                      variant="solid"
                      size="sm"
                      border="1px solid"
                      borderColor="gray.300"
                      onClick={() => handleRemovePeriod(index)}
                    >
                      Remove this period
                    </Button>
                </Flex>
              </Flex>
            )}

          </Box>
        );
      })}

      {overlappingIndexes?.length > 0 && (
        <Flex mt={2} p={2} borderLeft="4px solid" borderColor={'red.600'} borderRadius="md" bg={boxBg}>
          <Flex>
            <TbInfoTriangle color="#C42F30" size="18px" style={{ marginRight: '8px' }} />
          </Flex>
          <Flex direction={'column'} mb={1}>

            <Text fontSize="14px" color="red.600" fontWeight="semibold">
              Can't overlap your high-demand periods
            </Text>
      
            <Text fontSize="13px">
              Your high-demand periods can't overlap. Please delete or adjust the start and end times for at least one of these entries and try again.
            </Text>

            {overlappingIndexes?.map((idx) => {
              const p = specs[idx];
              const startDate = formatDate(timestampToDate(p?.time_start));
              const endDate = formatDate(timestampToDate(p?.time_end));
              return (
                <Text fontSize="12px" mt={1} key={idx}>
                  Spend {currencySymbol}{parseFloat(p?.budget_value) + 20} as the daily budget from {startDate} to {endDate}.
                </Text>
              );
            })}
          </Flex>
        </Flex>
      )}

      <Flex>
            {specs?.length < 50 && (
              <Flex justifyContent="space-between" width="100%">
               <Button
                leftIcon={<IoIosAddCircleOutline />}
                colorScheme="gray"
                variant="solid"
                size="sm"
                border="1px solid"
                borderColor="gray.300"
                onClick={handleAddPeriod}
              >
                  Add another time period
              </Button>
              <Text fontSize={'14px'}>{`${specs?.length}/50 time periods`}</Text>
              </Flex>
            )}
      </Flex>
    </Flex>
  );
};

export default BudgetScheduleSpecs;
